<template>
    <ModeSwitcher
        v-show="screenMode === 'OWN_CONTRACT'"
        ref="modeSwitcher"
        :create-mode-text="$t('product.info.create_hotel')"
        class="pt-4"
    >
        <template v-slot:btn-block>
            <v-btn data-cy="create" color="primary" class="zsh-create-btn" @click="createWithoutAssociate()">
                {{ $t('create') }}
            </v-btn>
            <!--<v-btn color="primary" variant="text" icon="mdi-file-import" class="ml-4" @click="showNotification()" />-->
        </template>
        <template v-slot:main-mode>
            <ProductHotelsFilter
                v-model="filters"
                :has-active-filters="hasActiveFilters"
                @submit="loadData()"
                @reset="resetFilters"
            />
            <v-data-table-server
                :headers="useHeaders"
                :items="contracts"
                :items-per-page-options="useTableOptions(totalCount)"
                :options="tableOptions"
                :loading="isLoading"
                :items-length="totalCount"
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="contracts-table"
                must-sort
                @update:options="onUpdateTableOptions"
            >
                <template v-slot:item.name="{item}">
                    <nuxt-link
                        no-prefetch
                        :to="{name: 'supplierContract', params: {contractId: item.id}}"
                        :data-cy="`contract-hotel-${item.id}`"
                    >
                        {{ item.name }}
                    </nuxt-link>
                    <div class="text-medium-emphasis">{{ item.productName }}</div>
                </template>
                <template v-slot:item.category="{item}">
                    <template v-if="item.category || item.category === 0">
                        {{ item.category === 0 ? $t('other') : `${item.category}*` }}
                    </template>
                </template>
                <template v-slot:item.stage="{item}">
                    {{ item.stage && $t(`stageType.${item.stage}`) }}
                </template>
                <template v-slot:item.active="{item}">
                    <v-switch
                        v-model="item.active"
                        color="primary"
                        :label="item.active ? $t('active') : $t('inactive')"
                        hide-details
                        :data-cy="`contract-status-${item.id}`"
                        :disabled="!authStore?.isTO1 && !item.active"
                        @update:model-value="changeStatus(item)"
                    />
                </template>
                <template v-slot:item.actions="{item}">
                    <ActionButtons
                        :is-show-rates-and-availability="true"
                        :is-show-prices="true"
                        :is-has-business-intelligence-access="useIsHasBusinessIntelligenceAccess"
                        :product-id="item.id"
                    />
                </template>
            </v-data-table-server>
        </template>
        <template v-slot:additional-mode>
            <InfoContractForm
                :service-type="contractServiceType.ownHotel"
                :contract-id="0"
                :associate-with-hotel="associateWithHotel"
                @cancel="modeSwitcher.goToTable()"
            />
        </template>
    </ModeSwitcher>
    <div v-show="screenMode === 'HOTELS'" class="px-1 pb-1">
        <ProductHotelsDescriptionsFilter
            v-model="descriptionFilters"
            :has-active-filters="hasActiveDescriptionFilters"
            :is-hide-inactive-filter="true"
            :is-show-full-list-of-content-providers="true"
            @submit="loadHotels()"
            @reset="resetDescriptionFilters"
        />
        <v-data-table-server
            :headers="useHotelHeaders"
            :items="hotels"
            :items-per-page-options="useTableOptions(totalHotelsCount)"
            :options="tableHotelsOptions"
            :loading="isHotelsLoading"
            :items-length="totalHotelsCount"
            disable-sort
            density="compact"
            color="primary"
            class="elevation-1 table-server"
            data-cy="hotels-table"
            @update:options="onUpdateTableHotelsOptions"
        >
            <template v-slot:item.name="{item}">
                <div class="d-flex justify-space-between">
                    <nuxt-link
                        no-prefetch
                        :data-cy="`hotel-${item.id}`"
                        :to="{name: 'hotelDescription', params: {hotelId: item.id}}"
                    >
                        {{ item.name }}
                    </nuxt-link>
                    <v-icon
                        v-if="item.haveHotelsInGroup"
                        class="mt-1 cursor-pointer"
                        color="primary"
                        @click="showHotelMapping(item.groupId)"
                    >
                        mdi-domain
                    </v-icon>
                </div>
            </template>
            <template v-slot:item.contentProviderId="{item}">
                {{
                    item.contentProviderId === 1
                        ? $t('own_description')
                        : getContentProviderName(item.contentProviderId)
                }}
            </template>
            <template v-slot:item.category="{item}">
                <template v-if="item.category || item.category === 0">
                    {{ item.category === 0 ? $t('other') : `${item.category}*` }}
                </template>
            </template>
            <template v-slot:item.active="{item}">
                <v-switch
                    v-model="item.active"
                    color="primary"
                    :label="item.active ? $t('active') : $t('inactive')"
                    hide-details
                    :data-cy="`contract-status-${item.id}`"
                    :disabled="!authStore?.isTO1 && !item.active"
                    @update:model-value="changeDescriptionHotelStatus(item)"
                />
            </template>
        </v-data-table-server>
        <ModalWrapper v-model="isShowModal" :max-width="1200">
            <template v-slot:title>
                {{ $t('hotel_mappings') }}
            </template>
            <template v-slot:content>
                <v-data-table-server
                    :headers="useHotelMappingHeaders"
                    :items="hotelMapping"
                    :items-per-page-options="useTableOptions(hotelMappingCount)"
                    :options="hotelMappingTableOptions"
                    :loading="isHotelMappingLoading"
                    :items-length="hotelMapping.length"
                    disable-sort
                    density="compact"
                    class="elevation-1 table-server"
                    data-cy="hotel-mapping-table"
                    @update:options="onUpdateHotelMappingTableHotelsOptions"
                >
                    <template v-slot:item.name="{item}">
                        <span :data-cy="`hotel-mapping-${item.id}`" class="text-link">
                            {{ item.name }}
                        </span>
                    </template>
                    <template v-slot:item.contentProviderId="{item}">
                        {{
                            item.contentProviderId === 1
                                ? $t('own_description')
                                : getContentProviderName(item.contentProviderId)
                        }}
                    </template>
                </v-data-table-server>
            </template>
        </ModalWrapper>
    </div>
</template>

<script setup lang="ts">
    import {useIsHasBusinessIntelligenceAccess, useTableOptions} from '~/composables/helpers'
    import ActionButtons from '~/components/products/ActionButtons.vue'
    import {initAccommodationDescriptionFilter, initAccommodationFilter} from '~/utils/types/account/products/helpers'
    import type {
        AccommodationDescriptionFilterFilterType,
        AccommodationFilterType,
    } from '~/utils/types/account/products/types'
    import {EventBus, SHOW_NOTIFICATION_MODAL} from '~/utils/event-bus'
    import {contractServiceType} from '~/utils/types/types'
    import InfoContractForm from '~src/components/products/information/InfoContractForm.vue'
    import ProductHotelsFilter from '~/components/products/hotels/ProductHotelsFilter.vue'
    import ProductHotelsDescriptionsFilter from '~/components/products/hotels/ProductHotelsDescriptionsFilter.vue'
    import {isEqual} from '@/utils/helpers'

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
        sortable?: boolean
    }

    const isLoading = ref<boolean>(false)
    const contracts = ref<any[]>([])
    const totalCount = ref<number>(0)
    const tableOptions = ref({
        itemsPerPage: 10,
        page: 1,
        sortBy: [{key: 'name', order: 'asc'}],
    })
    const sortedDirection = {
        asc: 'ASCENDING',
        desc: 'DESCENDING',
    }
    const totalHotelsCount = ref<number>(0)
    const tableHotelsOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const filters = ref<AccommodationFilterType>(initAccommodationFilter())
    const descriptionFilters = ref<AccommodationDescriptionFilterFilterType>(initAccommodationDescriptionFilter())
    const modeSwitcher = ref(null)
    const screenMode = ref<string>('OWN_CONTRACT')
    const isHotelsLoading: Ref<boolean> = ref(false)
    const hotels = ref([])
    const isShowModal = ref<boolean>(false)
    const hotelMapping = ref([])
    const hotelMappingCount = ref<number>(0)
    const isHotelMappingLoading = ref<boolean>(false)
    const hotelMappingTableOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const selectedGroupId = ref(null)
    const associateWithHotel = ref(false)

    const app = useNuxtApp()
    const {t} = useI18n()

    async function loadHotels(): Promise<void> {
        try {
            const {itemsPerPage, page} = tableHotelsOptions.value
            isHotelsLoading.value = true
            const rq = {
                ...descriptionFilters.value,
                ...(descriptionFilters.value.name && {namePattern: descriptionFilters.value.name}),
                ...(descriptionFilters.value.contentProviderId && {
                    contentProviderId: descriptionFilters.value.contentProviderId,
                }),
            }
            delete rq.name
            clean(rq)
            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})
            const res = await app.$api.hotels.getFull(rq)
            hotels.value = res.hotels
            totalHotelsCount.value = res.count
        } catch (e) {
            hotels.value = []
        } finally {
            isHotelsLoading.value = false
        }
    }

    async function loadData(): Promise<void> {
        const {itemsPerPage, page, sortBy} = tableOptions.value
        const {key, order} = sortBy[0]

        try {
            isLoading.value = true
            const supplierId = (filters.value.supplierId as {id?: number})?.id
            const rq = {
                ...filters.value,
                ...(filters.value.name && {pattern: filters.value.name}),
                ...(supplierId && {supplierId}),
                sortingParam: key.toUpperCase(),
                sortOrder: sortedDirection[order],
            }
            clean(rq)
            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})

            const {ownProductContracts, count} = await app.$api.ownAccommodationContracts.get(rq)
            totalCount.value = count
            contracts.value = ownProductContracts.map(c => ({
                ...c,
                departmentName: getDepartmentNameById(c.departmentId),
            }))
        } catch (e) {
            contracts.value = []
        } finally {
            isLoading.value = false
        }
    }

    function createWithoutAssociate() {
        associateWithHotel.value = false
        nextTick().then(() => {
            modeSwitcher.value.create()
        })
    }
    function createWithAssociate() {
        associateWithHotel.value = true
        nextTick().then(() => {
            modeSwitcher.value.create()
        })
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '150px', sortable: true},
            {title: t('filterTitles.supplierName'), value: 'supplierName'},
            {title: t('country'), value: 'countryName'},
            {title: t('city'), value: 'cityName'},
            {title: t('address'), value: 'address'},
            {title: t('category'), value: 'category'},
            {title: t('department'), value: 'departmentName'},
            {title: t('stage'), value: 'stage'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
            ...(!(app.$conf.public.account.productsManagementForSuppliersHotelsRAOnly && authStore.isSupplier)
                ? [{title: '', value: 'actions', width: '140px', minWidth: '140px'}]
                : []),
        ]
        return headers
    })

    const useHotelHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('source_of_description'), value: 'contentProviderId'},
            {title: t('hotel_code'), value: 'hotelCode'},
            {title: t('group_number'), value: 'groupId'},
            {title: `GIATA ${t('code')}`, value: 'giataCode'},
            {title: t('country'), value: 'location.countryName'},
            {title: t('city'), value: 'location.cityName'},
            {title: t('address'), value: 'location.address'},
            {title: t('category'), value: 'category'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const useHotelMappingHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('source_of_description'), value: 'contentProviderId'},
            {title: t('hotel_code'), value: 'hotelCode'},
            {title: t('country'), value: 'location.countryName'},
            {title: t('city'), value: 'location.cityName'},
            {title: t('group_number'), value: 'groupId'},
            {title: `GIATA ${t('code')}`, value: 'giataCode'},
        ]
    })

    const changeStatus = async (item): Promise<void> => {
        const {id, active, hotelContentId} = item
        try {
            isLoading.value = true
            await app.$api.ownAccommodationContracts.patch(id, {active})

            // When toggling contract status, also update the corresponding hotel info
            if (hotelContentId) {
                try {
                    await app.$api.ownHotelInfo.patch(hotelContentId, {active})
                } catch (hotelError) {
                    // Log the error but don't fail the contract activation
                    console.warn('Failed to update hotel info:', hotelError)
                }
            }

            await loadData()
            app.$toast.success(t('success_message.changed_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const changeDescriptionHotelStatus = async (item): Promise<void> => {
        const {id, active} = item
        try {
            isLoading.value = true
            await app.$api.ownHotelInfo.patch(id, {active})
            await loadHotels()
            app.$toast.success(t('success_message.changed_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const lastSorts = ref(null)
    const onUpdateTableOptions = (options): void => {
        tableOptions.value = {
            itemsPerPage: options.itemsPerPage,
            page: options.page,
            sortBy: [{key: options.sortBy?.[0]?.key || 'name', order: options.sortBy?.[0]?.order || 'asc'}],
        }
        if (!isEqual(tableOptions.value, lastSorts.value)) {
            lastSorts.value = tableOptions.value
            loadData()
        }
    }

    const onUpdateTableHotelsOptions = (options): void => {
        tableHotelsOptions.value = {itemsPerPage: options.itemsPerPage, page: options.page}
        loadHotels()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initAccommodationFilter(), filters.value)
    })

    const hasActiveDescriptionFilters = computed(() => {
        return !isEqual(initAccommodationDescriptionFilter(), descriptionFilters.value)
    })

    const resetFilters = (): void => {
        filters.value = initAccommodationFilter()
        loadData()
    }

    const resetDescriptionFilters = (): void => {
        descriptionFilters.value = initAccommodationDescriptionFilter()
        loadHotels()
    }

    const showNotification = () => {
        EventBus.$emit(SHOW_NOTIFICATION_MODAL, t('product.import_note'))
    }

    const showHotelMapping = async groupId => {
        selectedGroupId.value = groupId
        isShowModal.value = true
    }

    const onUpdateHotelMappingTableHotelsOptions = (options): void => {
        hotelMappingTableOptions.value = {itemsPerPage: options.itemsPerPage, page: options.page}
        loadHotelMapping()
    }

    async function loadHotelMapping(): Promise<void> {
        const {itemsPerPage, page} = hotelMappingTableOptions.value
        const rq = {groupId: selectedGroupId.value}
        if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})
        try {
            isHotelMappingLoading.value = true
            const {hotels, count} = await app.$api.hotels.getFull(rq)
            hotelMapping.value = hotels
            hotelMappingCount.value = count
        } catch (e) {
            hotelMapping.value = []
            hotelMappingCount.value = 0
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isHotelMappingLoading.value = false
        }
    }

    const getContentProviderName = contentProviderId => {
        return supplierStore.contentProviders?.find(({id}) => id === contentProviderId).name
    }

    const {getDepartmentNameById} = useDepartments()
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';
    :deep(div) {
        .radio-btn .v-label {
            width: max-content;
        }
    }

    // ZSH theme styling
    :deep(.v-data-table) {
        .v-btn.bg-primary {
            background-color: var(--zsh-primary-dark) !important;
        }
    }

    .zsh-create-btn {
        background-color: var(--zsh-primary-dark) !important;
        color: white !important;
    }
</style>
